import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only auto-redirect to login if we're NOT already on the login page
    // and the 401 is NOT from the login endpoint itself
    if (error.response?.status === 401 &&
        !error.config?.url?.includes('/auth/login') &&
        !error.config?.url?.includes('/staff/kiosk/') &&
        window.location.pathname !== '/login' &&
        window.location.pathname !== '/kiosk') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email, password, firstName, lastName) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
    });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Pallet API
export const palletAPI = {
  getSheets: async (startDate, endDate, page = 1, limit = 20) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('page', page);
    params.append('limit', limit);
    const response = await api.get(`/pallets/sheets?${params.toString()}`);
    return response.data;
  },
  getSheet: async (id) => {
    const response = await api.get(`/pallets/sheets/${id}`);
    return response.data;
  },
  createSheet: async (data) => {
    const response = await api.post('/pallets/sheets', data);
    return response.data;
  },
  updateSheet: async (id, data) => {
    const response = await api.put(`/pallets/sheets/${id}`, data);
    return response.data;
  },
  deleteSheet: async (id) => {
    const response = await api.delete(`/pallets/sheets/${id}`);
    return response.data;
  },
  getSummary: async (id) => {
    const response = await api.get(`/pallets/sheets/${id}/summary`);
    return response.data;
  },
  downloadPDF: async (id) => {
    const response = await api.get(`/pallets/sheets/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },
  sendEmail: async (id, recipients) => {
    const response = await api.post(`/pallets/sheets/${id}/email`, { recipients });
    return response.data;
  },
};

// Dropdown API
export const dropdownAPI = {
  getAll: async () => {
    const response = await api.get('/dropdowns');
    return response.data;
  },
  getByType: async (type, detailed = false) => {
    const response = await api.get(`/dropdowns/${type}${detailed ? '?detailed=true' : ''}`);
    return response.data;
  },
  create: async (listType, listValue, sortOrder) => {
    const response = await api.post('/dropdowns', { listType, listValue, sortOrder });
    return response.data;
  },
  update: async (id, listValue, sortOrder) => {
    const response = await api.put(`/dropdowns/${id}`, { listValue, sortOrder });
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/dropdowns/${id}`);
    return response.data;
  },
};

// User API
export const userAPI = {
  createUser: async (email, password, firstName, lastName, role) => {
    const response = await api.post('/users', {
      email,
      password,
      firstName,
      lastName,
      role
    });
    return response.data;
  },
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  updateUserRole: async (id, role) => {
    const response = await api.put(`/users/${id}/role`, { role });
    return response.data;
  },
  deactivateUser: async (id) => {
    const response = await api.put(`/users/${id}/deactivate`);
    return response.data;
  },
  activateUser: async (id) => {
    const response = await api.put(`/users/${id}/activate`);
    return response.data;
  },
  resetPassword: async (id, newPassword) => {
    const response = await api.put(`/users/${id}/reset-password`, { newPassword });
    return response.data;
  },
};

// Email API
export const emailAPI = {
  getRecipients: async () => {
    const response = await api.get('/email/recipients');
    return response.data;
  },
  createRecipient: async (email, name) => {
    const response = await api.post('/email/recipients', { email, name });
    return response.data;
  },
  updateRecipient: async (id, name) => {
    const response = await api.put(`/email/recipients/${id}`, { name });
    return response.data;
  },
  deleteRecipient: async (id) => {
    const response = await api.delete(`/email/recipients/${id}`);
    return response.data;
  },
};

// Reports API
export const reportsAPI = {
  getByCustomer: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/reports/by-customer?${params.toString()}`);
    return response.data;
  },
  getByGrower: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/reports/by-grower?${params.toString()}`);
    return response.data;
  },
  getByGrowerSize: async (startDate, endDate, grower = 'all') => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (grower) params.append('grower', grower);
    const response = await api.get(`/reports/by-grower-size?${params.toString()}`);
    return response.data;
  },
  getByDate: async (startDate, endDate, groupBy = 'day') => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('groupBy', groupBy);
    const response = await api.get(`/reports/by-date?${params.toString()}`);
    return response.data;
  },
  getByProduce: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/reports/by-produce?${params.toString()}`);
    return response.data;
  },
  getGrowerPivot: async (startDate, endDate, produceType = 'all') => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (produceType) params.append('produceType', produceType);
    const response = await api.get(`/reports/grower-pivot?${params.toString()}`);
    return response.data;
  },
  getSheetSummary: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/reports/sheet-summary?${params.toString()}`);
    return response.data;
  },
};

// Grower Invoices API
export const growerInvoiceAPI = {
  getNextNumber: async () => {
    const response = await api.get('/grower-invoices/next-number');
    return response.data;
  },
  findByGrower: async (grower, startDate, endDate) => {
    const params = new URLSearchParams();
    params.append('grower', grower);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    const response = await api.get(`/grower-invoices?${params.toString()}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/grower-invoices', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/grower-invoices/${id}`, data);
    return response.data;
  },
  downloadPDF: async (id) => {
    const response = await api.get(`/grower-invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },
  sendEmail: async (id, extraRecipients = []) => {
    const response = await api.post(`/grower-invoices/${id}/email`, {
      extraRecipients,
    });
    return response.data;
  },
};

// Growers API (contact details per grower code)
export const growersAPI = {
  getAll: async (includeInactive = false) => {
    const url = `/growers${includeInactive ? '?includeInactive=true' : ''}`;
    const response = await api.get(url);
    return response.data;
  },
  getByCode: async (code) => {
    const response = await api.get(`/growers/by-code/${encodeURIComponent(code)}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/growers', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/growers/${id}`, data);
    return response.data;
  },
  deactivate: async (id) => {
    const response = await api.delete(`/growers/${id}`);
    return response.data;
  },
};

// Audit API
export const auditAPI = {
  getLogs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.action) params.append('action', filters.action);
    if (filters.entityType) params.append('entityType', filters.entityType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    const response = await api.get(`/audit?${params.toString()}`);
    return response.data;
  },
  getStats: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/audit/stats?${params.toString()}`);
    return response.data;
  },
};

// Staff API
export const staffAPI = {
  getAll: async () => {
    const response = await api.get('/staff');
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/staff/${id}`);
    return response.data;
  },
  create: async (firstName, lastName, email, phone, schedule) => {
    const response = await api.post('/staff', { firstName, lastName, email, phone, schedule });
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/staff/${id}`, data);
    return response.data;
  },
  regeneratePin: async (id) => {
    const response = await api.put(`/staff/${id}/regenerate-pin`);
    return response.data;
  },
  activate: async (id) => {
    const response = await api.put(`/staff/${id}/activate`);
    return response.data;
  },
  deactivate: async (id) => {
    const response = await api.put(`/staff/${id}/deactivate`);
    return response.data;
  },
  // Kiosk endpoints (no auth token needed)
  validatePin: async (pin) => {
    const response = await api.post('/staff/kiosk/validate-pin', { pin });
    return response.data;
  },
  checkIn: async (pin, photoFile) => {
    const formData = new FormData();
    formData.append('pin', pin);
    if (photoFile) formData.append('photo', photoFile);
    const response = await api.post('/staff/kiosk/check-in', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  checkOut: async (pin, photoFile) => {
    const formData = new FormData();
    formData.append('pin', pin);
    if (photoFile) formData.append('photo', photoFile);
    const response = await api.post('/staff/kiosk/check-out', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  // Admin attendance management
  // Pass only the fields you want to change. breakMinutes: null clears the override
  // (back to auto), a number stores that exact value (0 means admin confirmed no break).
  updateAttendance: async (id, { checkInTime, checkOutTime, breakMinutes } = {}) => {
    const body = {};
    if (checkInTime !== undefined) body.checkInTime = checkInTime;
    if (checkOutTime !== undefined) body.checkOutTime = checkOutTime;
    if (breakMinutes !== undefined) body.breakMinutes = breakMinutes;
    const response = await api.put(`/staff/attendance/${id}`, body);
    return response.data;
  },
  createManualAttendance: async (staffId, date, checkInTime, checkOutTime, breakMinutes) => {
    const response = await api.post('/staff/attendance/manual', { staffId, date, checkInTime, checkOutTime, breakMinutes });
    return response.data;
  },
  // Attendance reports
  getDailyAttendance: async (date) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    const response = await api.get(`/staff/attendance/daily?${params.toString()}`);
    return response.data;
  },
  getRangeAttendance: async (startDate, endDate) => {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    const response = await api.get(`/staff/attendance/range?${params.toString()}`);
    return response.data;
  },
};

export default api;